import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../../hooks/useAuth';

// Redux
import { store } from '../../../../../state/configureStore';
import { getCursor } from '../../../../../state/selectors/ui/tables';

// Types
import {
    tableIds,
    takeBase,
} from '../../../../../components/display/tables/types';

// GQL
import { client } from '../../../../../gql/client';
import { selectWorkOrdersWithMultimedia } from '../../../../../gql/selectors/multimedia';
import { GET_ORDER_BY_ID, GET_SELECTORS_DATA } from '../../../../../gql/queries/orders/orders';
import { FETCH_USERS } from '../../../../../gql/queries/users';
import {
    FETCH_CAPITOLS,
    FETCH_SUB_CAPITOLS,
    FETCH_CLASSIFICATIONS,
    FETCH_PREDEFINED_ORDERS,
} from '../../../../../gql/queries/orders/predefinedData';
import { loadingData } from '../../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { matchesPkRange } from '../../../../../utils/multimediaPkFilter';

/**
 * Filtrado client-side de los raw orders antes de pasar al selector.
 * Aplica los mismos filtros que la UI del tab Multimedia dentro de una OT/Campaña.
 * Semántica PK = solapamiento de rangos (mismo patrón que backend: accidents/campaigns.service).
 */
const applyCursorFilters = (orders: any[], cursor: any): any[] => {
    if (!cursor) return orders;


    // Multi-select fields: empty array or undefined → no filter. Match if order's value is in the array.
    const matchesStates = (o: any): boolean => {
        const states: string[] | undefined = cursor.states;
        if (!states || states.length === 0) return true;
        return states.includes(o.state);
    };
    const matchesUserIds = (o: any): boolean => {
        const userIds: string[] | undefined = cursor.userIds;
        if (!userIds || userIds.length === 0) return true;
        return userIds.includes(o.userId);
    };

    return orders.filter((o) => (
        matchesPkRange(o, cursor) &&
        matchesStates(o) &&
        (cursor.capitolId == null || o.capitolIDs?.includes(cursor.capitolId)) &&
        (cursor.subCapitolId == null || o.subCapitolIDs?.includes(cursor.subCapitolId)) &&
        (cursor.classification == null || o.classificationIDs?.includes(cursor.classification)) &&
        (cursor.predefinedOrderId == null || o.predefinedOrderIDs?.includes(cursor.predefinedOrderId)) &&
        (cursor.roadId == null || o.roadIDs?.includes(cursor.roadId)) &&
        matchesUserIds(o) &&
        (cursor.concessionId == null || o.concessionIDs?.includes(cursor.concessionId)) &&
        (cursor.indexType == null || o.indexType === cursor.indexType) &&
        (cursor.indexExpedition == null || o.indexExpedition === cursor.indexExpedition) &&
        (cursor.fromDate == null || new Date(o.registerInitDate) >= new Date(cursor.fromDate)) &&
        (cursor.toDate == null || new Date(o.registerInitDate) <= new Date(cursor.toDate))
    ));
};

export const LoaderOrderMultimediaPage: (
    userData: AuthContextType | null
) => LoaderFunction = (userData) => async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());
    const dispatch = store.dispatch;

    const roleActive = userData?.user?.user?.rolActive;

    if (userData?.user) {
        // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por
        // un run anterior interrumpido (este loader siempre abre el flag,
        // así que un throw a mitad lo dejaba colgado).
        dispatch(loadingData(tableIds.MULTIMEDIA, false));
        dispatch(loadingData(tableIds.MULTIMEDIA, true));
        try {
        const { data: orderData } = await client.query({
            query: GET_ORDER_BY_ID,
            variables: { id: params.id },
            fetchPolicy: 'network-only',
        });

        const parentOrder = orderData?.order;
        const rawOrders = [];

        if (parentOrder) {
            rawOrders.push(parentOrder);

            if (parentOrder.childExpeditionOrderIDs?.length > 0) {
                const children = await Promise.all(
                    parentOrder.childExpeditionOrderIDs.map(async (childId: string) => {
                        const { data: childData } = await client.query({
                            query: GET_ORDER_BY_ID,
                            variables: { id: childId },
                            fetchPolicy: 'network-only',
                        });
                        return childData?.order;
                    })
                );
                rawOrders.push(...children.filter(Boolean));
            }
        }

        // Cargar usuarios ANTES del selector: la columna "Usuario" se resuelve
        // vía usersMap (client.readQuery(FETCH_USERS)) dentro del selector, y el
        // dropdown de filtro se renderiza si filtersDataOptions.users tiene datos.
        const {
            data: { users },
        } = await client.query({
            query: FETCH_USERS,
            fetchPolicy: 'cache-first',
        });

        const filteredRawOrders = applyCursorFilters(rawOrders, cursorMultimedia);
        const dataOrderWithMultimedia = await selectWorkOrdersWithMultimedia(filteredRawOrders);

        const [
            {
                data: { capitols },
            },
            {
                data: { subCapitols },
            },
            {
                data: { roads, concessions },
            },
            {
                data: { classifications },
            },
            {
                data: { predefinedOrders },
            },
        ] = await Promise.all([
            client.query({
                query: FETCH_CAPITOLS,
                fetchPolicy: 'cache-first',
            }),
            client.query({
                query: FETCH_SUB_CAPITOLS,
                fetchPolicy: 'cache-first',
            }),
            client.query({
                query: GET_SELECTORS_DATA,
                fetchPolicy: 'cache-first',
            }),
            client.query({
                query: FETCH_CLASSIFICATIONS,
                fetchPolicy: 'cache-first',
            }),
            client.query({
                query: FETCH_PREDEFINED_ORDERS,
                fetchPolicy: 'cache-first',
            }),
        ]);

        const filtersDataOptions = {
            capitols,
            subCapitols,
            roads: roads,
            concessions,
            classifications,
            predefinedOrders,
            users,
        };

        const combinedData = {
            totalCount: dataOrderWithMultimedia.length,
            dataOrderWithMultimedia,
            pageInfo: {
                hasNextPage: false,
                hasPreviousPage: false,
            },
            filtersDataOptions,
            loading: false,
            roleActive,
            parentOrderRoid: parentOrder?.roid,
            parentOrderType: parentOrder?.type,
        };

        return combinedData;
        } finally {
            // FIX (loader-loading-flag-leak): cierra el flag en éxito y en throw.
            dispatch(loadingData(tableIds.MULTIMEDIA, false));
        }
    }
    return {};
};

  return { data: load() };
};
